import { useId, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Minimal tooltip for PRD UI-02 (inline explanations of every score, badge,
 * and icon). Pure CSS hover/focus trigger — no portals required so the
 * component stays tiny. We can swap this out for Radix later if v1 needs
 * keyboard-polished popovers.
 */
export function Tooltip({
  children,
  content,
  className,
}: {
  children: ReactNode;
  content: ReactNode;
  className?: string;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex" aria-describedby={open ? id : undefined}>
      <span
        tabIndex={0}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex cursor-help"
      >
        {children}
      </span>
      {open && (
        <span
          id={id}
          role="tooltip"
          className={cn(
            "pointer-events-none absolute left-1/2 top-[calc(100%+4px)] z-50 w-max max-w-xs -translate-x-1/2 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs text-foreground shadow-md",
            className,
          )}
        >
          {content}
        </span>
      )}
    </span>
  );
}
